import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { subscriptionsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, 
  Calendar, 
  CreditCard, 
  Edit2, 
  Save, 
  X,
  XCircle,
  MessageSquare,
  Trash2,
  Mail,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const SubscriptionDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [isMarkingCancelled, setIsMarkingCancelled] = useState(false);

  useEffect(() => {
    loadSubscription();
  }, [id]);

  const loadSubscription = async () => {
    try {
      const response = await subscriptionsApi.getOne(id);
      setSubscription(response.data);
      setEditForm(response.data);
    } catch (error) {
      toast.error('Failed to load subscription');
      navigate('/subscriptions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await subscriptionsApi.update(id, {
        name: editForm.name,
        amount: parseFloat(editForm.amount),
        currency: editForm.currency,
        billing_cycle: editForm.billing_cycle,
        renewal_date: editForm.renewal_date,
        category: editForm.category,
        notes: editForm.notes
      });
      setSubscription(editForm);
      setIsEditing(false);
      toast.success('Subscription updated');
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) return;
    
    try {
      await subscriptionsApi.delete(id);
      toast.success('Subscription deleted');
      navigate('/subscriptions');
    } catch (error) {
      toast.error('Failed to delete subscription');
    }
  };

  const handleMarkCancelled = async () => {
    setIsMarkingCancelled(true);
    try {
      await subscriptionsApi.markCancelled(id);
      setSubscription({ ...subscription, status: 'cancelled' });
      toast.success('Subscription marked as cancelled. Great job taking control of your subscriptions!');
    } catch (error) {
      toast.error('Failed to mark subscription as cancelled');
    } finally {
      setIsMarkingCancelled(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-[#D1FAE5] text-[#064E3B] border-0">Active</Badge>;
      case 'cancelled':
        return <Badge className="bg-red-100 text-red-700 border-0">Cancelled</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700 border-0">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getDaysUntilRenewal = () => {
    if (!subscription?.renewal_date) return null;
    const renewalDate = new Date(subscription.renewal_date);
    const today = new Date();
    const diffTime = renewalDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#064E3B]"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!subscription) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <p className="text-gray-500">Subscription not found</p>
          <Button onClick={() => navigate('/subscriptions')} className="mt-4">
            Back to Subscriptions
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  const daysUntilRenewal = getDaysUntilRenewal();

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="subscription-detail-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/subscriptions')}
            className="rounded-xl"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Outfit' }}>
              {subscription.name}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              {getStatusBadge(subscription.status)}
              {subscription.source === 'gmail_scan' && (
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  <Mail className="w-3 h-3" />
                  Auto-detected
                </span>
              )}
            </div>
          </div>
          {!isEditing && (
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setIsEditing(true)}
                className="rounded-xl"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                className="rounded-xl"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Main Details */}
          <div className="md:col-span-2 space-y-6">
            <Card className="border border-gray-100 shadow-soft">
              <CardHeader>
                <CardTitle className="text-lg">Subscription Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {isEditing ? (
                  <>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Name</Label>
                        <Input
                          value={editForm.name || ''}
                          onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Category</Label>
                        <Select 
                          value={editForm.category || 'Other'} 
                          onValueChange={(v) => setEditForm({...editForm, category: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Entertainment">Entertainment</SelectItem>
                            <SelectItem value="Productivity">Productivity</SelectItem>
                            <SelectItem value="Utilities">Utilities</SelectItem>
                            <SelectItem value="Health">Health & Fitness</SelectItem>
                            <SelectItem value="Finance">Finance</SelectItem>
                            <SelectItem value="Shopping">Shopping</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Amount</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editForm.amount || ''}
                          onChange={(e) => setEditForm({...editForm, amount: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Currency</Label>
                        <Select 
                          value={editForm.currency || 'USD'} 
                          onValueChange={(v) => setEditForm({...editForm, currency: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                            <SelectItem value="CAD">CAD</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Billing Cycle</Label>
                        <Select 
                          value={editForm.billing_cycle || 'monthly'} 
                          onValueChange={(v) => setEditForm({...editForm, billing_cycle: v})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="yearly">Yearly</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Next Renewal Date</Label>
                      <Input
                        type="date"
                        value={editForm.renewal_date?.split('T')[0] || ''}
                        onChange={(e) => setEditForm({...editForm, renewal_date: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Input
                        value={editForm.notes || ''}
                        onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                        placeholder="Add notes..."
                      />
                    </div>
                    <div className="flex gap-2 pt-4">
                      <Button onClick={handleSave} className="bg-[#064E3B] hover:bg-[#053D2E] rounded-xl">
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </Button>
                      <Button variant="outline" onClick={() => setIsEditing(false)} className="rounded-xl">
                        <X className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-gray-500">Amount</p>
                        <p className="text-2xl font-bold text-gray-900">
                          ${subscription.amount}
                          <span className="text-sm font-normal text-gray-500 ml-1">
                            {subscription.currency}/{subscription.billing_cycle}
                          </span>
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Category</p>
                        <p className="text-lg font-medium text-gray-900">
                          {subscription.category || 'Other'}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <p className="text-sm text-gray-500">Next Renewal</p>
                        <p className="text-lg font-medium text-gray-900">
                          {formatDate(subscription.renewal_date)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-500">Added On</p>
                        <p className="text-lg font-medium text-gray-900">
                          {formatDate(subscription.created_at)}
                        </p>
                      </div>
                    </div>
                    {subscription.notes && (
                      <div>
                        <p className="text-sm text-gray-500">Notes</p>
                        <p className="text-gray-700">{subscription.notes}</p>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* Actions */}
            {subscription.status === 'active' && !isEditing && (
              <Card className="border border-gray-100 shadow-soft">
                <CardHeader>
                  <CardTitle className="text-lg">Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => navigate(`/cancel/${id}`)}
                      variant="outline"
                      className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      How to Cancel
                    </Button>
                    <Button
                      onClick={() => navigate(`/negotiate/${id}`)}
                      variant="outline"
                      className="rounded-xl"
                    >
                      <MessageSquare className="w-4 h-4 mr-2" />
                      Negotiate Price
                    </Button>
                  </div>
                  <div className="border-t pt-4">
                    <p className="text-sm text-gray-500 mb-3">Already cancelled this subscription?</p>
                    <Button
                      onClick={handleMarkCancelled}
                      disabled={isMarkingCancelled}
                      variant="outline"
                      className="rounded-xl border-green-200 text-green-700 hover:bg-green-50 w-full"
                      data-testid="mark-cancelled-btn"
                    >
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      {isMarkingCancelled ? 'Updating...' : "I've Cancelled This"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Renewal Alert */}
            {daysUntilRenewal !== null && daysUntilRenewal <= 7 && daysUntilRenewal > 0 && (
              <Card className="border-2 border-yellow-300 bg-yellow-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-yellow-200 rounded-xl flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-yellow-700" />
                    </div>
                    <div>
                      <p className="font-medium text-yellow-800">Renewing Soon</p>
                      <p className="text-sm text-yellow-700">
                        {daysUntilRenewal} day{daysUntilRenewal !== 1 ? 's' : ''} until renewal
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Cost Summary */}
            <Card className="border border-gray-100 shadow-soft">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#064E3B]" />
                  Cost Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-500">Per {subscription.billing_cycle}</span>
                  <span className="font-semibold">${subscription.amount}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Monthly cost</span>
                  <span className="font-semibold">
                    ${subscription.billing_cycle === 'monthly' 
                      ? subscription.amount 
                      : subscription.billing_cycle === 'yearly'
                      ? (subscription.amount / 12).toFixed(2)
                      : (subscription.amount * 4.33).toFixed(2)
                    }
                  </span>
                </div>
                <div className="border-t pt-3 flex justify-between">
                  <span className="text-gray-500">Yearly cost</span>
                  <span className="font-bold text-lg">
                    ${subscription.billing_cycle === 'yearly' 
                      ? subscription.amount 
                      : subscription.billing_cycle === 'monthly'
                      ? (subscription.amount * 12).toFixed(2)
                      : (subscription.amount * 52).toFixed(2)
                    }
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SubscriptionDetail;
